/**
 * Templates API surface — typed wrappers around /v1/templates per TZ-02 §C.
 * Phase 2 only consumes list + show; T2.4 will round out create/duplicate/
 * archive/delete.
 */
import { apiClient } from "./apiClient";

export interface TemplateSummary {
  id: string;
  name: string;
  description?: string | null;
  template_type?: string | null;
  industry?: string | null;
  tags?: string[] | null;
  status?: string | null;
  current_version?: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Raw list/duplicate item as TemplateResource emits it. Since ED-A15 the
 * API merges the denormalised columns (name, description, timestamps, …)
 * into ONE canonical `meta` object — nothing but `id` lives at the top
 * level. The portal list consumed the pre-ED-A15 flat shape and silently
 * rendered "— · Updated —" for every row (caught by the e2e critical
 * path, 2026-06-11). Lift the fields we list here.
 */
interface ApiTemplateItem {
  id: string;
  meta?: {
    name?: string;
    description?: string | null;
    template_type?: string | null;
    industry?: string | null;
    tags?: string[] | null;
    created_at?: string;
    updated_at?: string;
  };
}

function toSummary(item: ApiTemplateItem): TemplateSummary {
  const meta = item.meta ?? {};
  return {
    id: item.id,
    name: meta.name ?? "",
    description: meta.description ?? null,
    template_type: meta.template_type ?? null,
    industry: meta.industry ?? null,
    tags: meta.tags ?? null,
    created_at: meta.created_at ?? "",
    updated_at: meta.updated_at ?? "",
  };
}

/**
 * The list endpoint returns its pagination under the `pagination` key (TZ-02
 * §C; matches gallery.ts / migrations.ts). `total` is present only when the
 * request opted in via `with_total` — it's the filtered-set count, used by the
 * dashboard stat card (QA-019).
 */
export interface PaginatedMeta {
  limit?: number;
  next_cursor?: string | null;
  has_more: boolean;
  total?: number;
}

export interface TemplateListParams {
  limit?: number;
  cursor?: string;
  sort?: string;
  /** Ask the API for the true total count of the filtered set (QA-019). */
  withTotal?: boolean;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginatedMeta;
}

export async function listTemplates(
  params: TemplateListParams = {},
): Promise<PaginatedResponse<TemplateSummary>> {
  const search = new URLSearchParams();
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.cursor !== undefined) search.set("cursor", params.cursor);
  if (params.sort !== undefined) search.set("sort", params.sort);
  if (params.withTotal === true) search.set("with_total", "1");
  const query = search.toString();
  const path = query === "" ? "/v1/templates" : `/v1/templates?${query}`;
  const res = await apiClient.get<PaginatedResponse<ApiTemplateItem>>(path);
  return { ...res, data: res.data.map(toSummary) };
}

interface Wrapped<T> {
  data: T;
}

export async function deleteTemplate(id: string): Promise<void> {
  await apiClient.delete(`/v1/templates/${id}`);
}

export async function duplicateTemplate(id: string): Promise<TemplateSummary> {
  const res = await apiClient.post<Wrapped<ApiTemplateItem>>(
    `/v1/templates/${id}/duplicate`,
  );
  return toSummary(res.data);
}

/**
 * Opaque full-template payload — the canonical EmailTemplate shape from
 * shared-types is large; portal-side we treat it as a record blob until
 * the editor consumes it.
 */
export type FullTemplate = Record<string, unknown>;

/** Server-issued id is always present after create / read. */
export interface PersistedTemplate extends FullTemplate {
  id: string;
}

export async function getTemplate(id: string): Promise<FullTemplate> {
  const res = await apiClient.get<Wrapped<FullTemplate>>(`/v1/templates/${id}`);
  return res.data;
}

/**
 * PUT replaces the entire document. Per CONVENTIONS this is the safe
 * write path the editor uses; PATCH is reserved for cherry-picked CRUD
 * updates from the portal Templates list (Phase 2 §T2.4.1).
 */
export async function putTemplate(
  id: string,
  document: FullTemplate,
): Promise<FullTemplate> {
  const res = await apiClient.request<Wrapped<FullTemplate>>(
    `/v1/templates/${id}`,
    { method: "PUT", json: document },
  );
  return res.data;
}

/** POST creates a new template; server-side service mints id + version 1. */
export async function createTemplate(
  document: FullTemplate,
): Promise<PersistedTemplate> {
  const res = await apiClient.post<Wrapped<PersistedTemplate>>(
    "/v1/templates",
    document,
  );
  return res.data;
}

/**
 * "Use this template" per ED-A12 §D4 — the API clones the gallery
 * document server-side and stamps the requested name. Passing a brand
 * kit id merges its colors/fonts/logo into the clone's theme (§C3).
 */
export async function createTemplateFromGallery(
  name: string,
  galleryId: string,
  applyBrandKitId?: string,
): Promise<PersistedTemplate> {
  const res = await apiClient.post<Wrapped<PersistedTemplate>>("/v1/templates", {
    name,
    from_gallery_id: galleryId,
    ...(applyBrandKitId !== undefined
      ? { apply_brand_kit_id: applyBrandKitId }
      : {}),
  });
  return res.data;
}

/**
 * Compiled HTML for a single template — drives the scaled-iframe preview
 * thumbnails on the Templates page (lazy-rendered per card via IntersectionObserver).
 * Uses the same render endpoint the editor preview + send-test use, so the
 * thumbnail always reflects the live document (no stored-thumbnail infra).
 */
export async function renderTemplate(id: string): Promise<string> {
  const res = await apiClient.post<Wrapped<{ html: string }>>(
    `/v1/templates/${id}/render`,
    {},
  );
  return res.data.html;
}

/**
 * AI template generation: a natural-language brief becomes a complete, premium
 * template server-side (ai-service blueprint mode → deterministic compiler).
 * A brand kit, when given, is forwarded so the copy is on-brand.
 */
export async function generateTemplate(
  prompt: string,
  name?: string,
  brandKitId?: string,
): Promise<PersistedTemplate> {
  const res = await apiClient.post<Wrapped<PersistedTemplate>>("/v1/templates/generate", {
    prompt,
    ...(name !== undefined && name !== "" ? { name } : {}),
    ...(brandKitId !== undefined ? { brand_kit_id: brandKitId } : {}),
  });
  return res.data;
}

export interface TemplateVersionSummary {
  version_id: string;
  version_number: number;
  created_at: string | null;
  created_by_user_id: string | null;
  change_summary: string | null;
}

/** Version log per TZ-02 §C2 — newest first. */
export async function listTemplateHistory(
  id: string,
): Promise<TemplateVersionSummary[]> {
  const res = await apiClient.get<{ data: TemplateVersionSummary[] }>(
    `/v1/templates/${id}/history?limit=50`,
  );
  return res.data;
}

/** Append-only restore: the old document comes back as a NEW version. */
export async function restoreTemplateVersion(
  id: string,
  versionId: string,
): Promise<PersistedTemplate> {
  const res = await apiClient.post<Wrapped<PersistedTemplate>>(
    `/v1/templates/${id}/restore`,
    { version_id: versionId },
  );
  return res.data;
}
